import { AnimatePresence, motion } from 'framer-motion';
import { FileText, GripVertical, Loader2, Plus, Search, X } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { Blob } from '@/services/types/blob-types';

import { ExhibitDocumentPreview } from '../ExhibitDocumentPreview';
import type { ExhibitSelection, MultiExhibitDialogProps } from './types';
import { useMultiExhibitSelect } from './useMultiExhibitUpload';

function formatFileSize(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Resizable divider component
const ResizeHandle = ({ onResize }: { onResize: (deltaX: number) => void }) => {
  const isDragging = useRef(false);
  const lastX = useRef(0);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      isDragging.current = true;
      lastX.current = e.clientX;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!isDragging.current) return;
        const deltaX = moveEvent.clientX - lastX.current;
        lastX.current = moveEvent.clientX;
        onResize(deltaX);
      };

      const handleMouseUp = () => {
        isDragging.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [onResize],
  );

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className="w-1 hover:w-1.5 bg-gray-200 hover:bg-blue-400 cursor-col-resize transition-all flex items-center justify-center group"
      onMouseDown={handleMouseDown}
    >
      <GripVertical className="h-4 w-4 text-gray-400 group-hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
  );
};

export const MultiExhibitUploadDialog = ({
  matterId,
  workspaceId,
  existingExhibitBlobIds,
  existingExhibitCount,
  onSuccess,
  buttonLabel = 'Add Exhibits',
}: MultiExhibitDialogProps) => {
  const [leftPanelWidth, setLeftPanelWidth] = useState(350);
  const [centerPanelWidth, setCenterPanelWidth] = useState(300);

  const {
    isOpen,
    selections,
    searchQuery,
    isSubmitting,
    previewBlob,
    open,
    close,
    setSearchQuery,
    removeSelection,
    updateSelectionName,
    clearAllSelections,
    selectForPreview,
    setPreviewBlob,
    getBlobById,
    submit,
    availableBlobs,
    isBlobsLoading,
    blobsError,
    canSubmit,
  } = useMultiExhibitSelect({
    matterId,
    workspaceId,
    existingExhibitBlobIds,
    existingExhibitCount,
    onSuccess,
  });

  const handleLeftResize = useCallback((deltaX: number) => {
    setLeftPanelWidth((prev) => Math.max(250, Math.min(500, prev + deltaX)));
  }, []);

  const handleCenterResize = useCallback((deltaX: number) => {
    setCenterPanelWidth((prev) => Math.max(200, Math.min(450, prev + deltaX)));
  }, []);

  return (
    <>
      <Button onClick={open} className="gap-1.5">
        <Plus className="h-4 w-4" />
        {buttonLabel}
      </Button>

      <Dialog
        open={isOpen}
        onOpenChange={(isDialogOpen) => !isDialogOpen && close()}
      >
        <DialogContent className="max-w-[95vw] w-[95vw] h-[90vh] max-h-[900px] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 py-4 border-b shrink-0">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-lg font-semibold">
                Add Exhibits
              </DialogTitle>
              {selections.length > 0 && (
                <span className="text-sm text-gray-500">
                  {selections.length} selected
                </span>
              )}
            </div>
          </DialogHeader>

          <div className="flex flex-1 min-h-0">
            {/* Left: Document picker */}
            <div
              className="flex flex-col shrink-0"
              style={{ width: leftPanelWidth }}
            >
              {/* Search */}
              <div className="p-3 border-b">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search documents..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 h-9"
                  />
                </div>
              </div>

              {/* Document list */}
              <div className="flex-1 overflow-y-auto">
                {isBlobsLoading ? (
                  <div className="flex items-center justify-center py-8 text-gray-500">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    Loading...
                  </div>
                ) : blobsError ? (
                  <div className="p-4 text-sm text-red-600">{blobsError}</div>
                ) : availableBlobs.length === 0 ? (
                  <div className="p-4 text-sm text-gray-500 text-center">
                    {searchQuery
                      ? 'No documents match your search'
                      : 'No documents available'}
                  </div>
                ) : (
                  <div className="divide-y">
                    {availableBlobs.map((blob) => (
                      <DocumentItem
                        key={blob.id}
                        blob={blob}
                        onSelect={() => selectForPreview(blob)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Resize handle */}
            <ResizeHandle onResize={handleLeftResize} />

            {/* Center: Selected items */}
            <div
              className="flex flex-col shrink-0 bg-gray-50/50"
              style={{ width: centerPanelWidth }}
            >
              <div className="px-4 py-3 border-b flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">
                  Selected ({selections.length})
                </span>
                {selections.length > 0 && (
                  <button
                    type="button"
                    onClick={clearAllSelections}
                    className="text-xs text-gray-500 hover:text-red-600"
                  >
                    Clear all
                  </button>
                )}
              </div>

              <div className="flex-1 overflow-y-auto p-2">
                {selections.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-gray-400 text-sm">
                    <FileText className="h-8 w-8 mb-2 opacity-50" />
                    <p>Click documents to select</p>
                  </div>
                ) : (
                  <AnimatePresence mode="popLayout">
                    {selections.map((selection) => (
                      <SelectedItem
                        key={selection.id}
                        selection={selection}
                        isPreview={previewBlob?.id === selection.id}
                        onUpdateName={(name) =>
                          updateSelectionName(selection.id, name)
                        }
                        onRemove={() => removeSelection(selection.id)}
                        onPreview={() => {
                          const blob = getBlobById(selection.id);
                          if (blob) setPreviewBlob(blob);
                        }}
                      />
                    ))}
                  </AnimatePresence>
                )}
              </div>
            </div>

            {/* Right: Preview (collapsible) */}
            {previewBlob && (
              <>
                <ResizeHandle onResize={handleCenterResize} />
                <motion.div
                  initial={{ width: 0, opacity: 0 }}
                  animate={{ width: 'auto', opacity: 1, flex: 1 }}
                  exit={{ width: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex flex-col min-w-0"
                >
                  <div className="px-4 py-3 border-b flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">
                      Preview
                    </span>
                    <button
                      type="button"
                      onClick={() => setPreviewBlob(null)}
                      className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                      title="Close preview"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4">
                    <ExhibitDocumentPreview
                      matterId={matterId}
                      blob={previewBlob}
                    />
                  </div>
                </motion.div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t flex items-center justify-between shrink-0 bg-gray-50/50">
            <p className="text-sm text-gray-500">
              {selections.length === 0
                ? 'Select documents from the list'
                : 'Click names to edit before adding'}
            </p>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={close} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button onClick={submit} disabled={!canSubmit}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-1" />
                    Add {selections.length > 0 ? selections.length : ''} Exhibit
                    {selections.length !== 1 && 's'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Document item in the picker list
const DocumentItem = ({
  blob,
  onSelect,
}: {
  blob: Blob;
  onSelect: () => void;
}) => {
  return (
    <button
      type="button"
      className="w-full flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors text-left hover:bg-gray-50"
      onClick={onSelect}
    >
      <FileText className="h-4 w-4 text-gray-400 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-sm text-gray-900 truncate">{blob.name}</p>
        {blob.file_size && (
          <p className="text-xs text-gray-500">
            {formatFileSize(blob.file_size)}
          </p>
        )}
      </div>
      <Plus className="h-4 w-4 text-gray-400 shrink-0" />
    </button>
  );
};

// Selected item with editable name
const SelectedItem = ({
  selection,
  isPreview,
  onUpdateName,
  onRemove,
  onPreview,
}: {
  selection: ExhibitSelection;
  isPreview: boolean;
  onUpdateName: (name: string) => void;
  onRemove: () => void;
  onPreview: () => void;
}) => {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: 20, scale: 0.95 }}
      transition={{ duration: 0.15 }}
      className={cn(
        'group flex items-start gap-2 p-2 rounded-lg mb-1.5 transition-colors cursor-pointer',
        isPreview
          ? 'bg-blue-100 border border-blue-200'
          : 'bg-white border border-gray-200 hover:border-gray-300',
      )}
      onClick={onPreview}
    >
      <div className="shrink-0 mt-0.5 p-1">
        <FileText
          className={cn(
            'h-4 w-4',
            isPreview ? 'text-blue-600' : 'text-gray-400',
          )}
        />
      </div>

      <div className="min-w-0 flex-1">
        <input
          type="text"
          value={selection.name}
          onChange={(e) => onUpdateName(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          className={cn(
            'w-full text-sm font-medium bg-transparent border-none p-0 focus:outline-none focus:ring-0',
            isPreview ? 'text-blue-900' : 'text-gray-900',
          )}
          placeholder="Exhibit name..."
        />
        <p className="text-xs text-gray-500 truncate">
          {selection.originalName}
        </p>
      </div>

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="shrink-0 p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <X className="h-4 w-4" />
      </button>
    </motion.div>
  );
};
